/**
 * 👁️ SPACE VISITORS - Module Visiteurs en Direct
 * Surveillance des sessions visiteurs style vaisseau spatial
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Globe, Monitor, Smartphone, Tablet, Clock, 
  MapPin, AlertTriangle, Eye, RefreshCw, ChevronDown, ChevronUp,
  Shield, Activity, TrendingUp, Radio, Wifi, WifiOff
} from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Carte statistique
const StatCard = ({ icon: Icon, label, value, subValue, color = 'green', pulse }) => {
  const colorClasses = {
    green: 'border-green-500/30 bg-green-500/10 text-green-400',
    blue: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
    purple: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
    amber: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
    red: 'border-red-500/30 bg-red-500/10 text-red-400',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`backdrop-blur-xl rounded-2xl p-4 border ${colorClasses[color]}`}
    >
      <div className="flex items-center gap-2 mb-2">
        {pulse && <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />}
        <Icon className="w-4 h-4" />
        <span className="text-sm">{label}</span>
      </div>
      <p className="text-3xl font-bold text-white">{value}</p>
      {subValue && <p className="text-xs mt-1 opacity-70">{subValue}</p>}
    </motion.div>
  );
};

// Carte de session active
const ActiveSessionCard = ({ session, getDeviceIcon, formatDuration }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    animate={{ opacity: 1, x: 0 }}
    className="bg-[#1a1a25] rounded-xl p-3 border border-green-500/30 flex items-center justify-between"
  >
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-green-500/20 text-green-400">
        {getDeviceIcon(session.deviceType)}
      </div>
      <div>
        <p className="text-white text-sm">
          {session.customerId ? (
            <span className="text-cyan-400 font-medium">Client connecté</span>
          ) : (
            <span className="text-gray-400">Visiteur anonyme</span>
          )}
        </p>
        <p className="text-gray-500 text-xs">
          {session.city}, {session.country} • {session.browser}
        </p>
      </div>
    </div>
    <div className="text-right">
      <p className="text-green-400 text-sm font-medium">{formatDuration(session.duration)}</p>
      <p className="text-gray-500 text-xs">{session.pageViews} pages</p>
    </div>
  </motion.div>
);

// Barre de progression
const ProgressBar = ({ label, value, max, color = 'cyan' }) => {
  const percent = max > 0 ? (value / max) * 100 : 0;
  const colorClasses = {
    cyan: 'from-cyan-500 to-blue-500',
    green: 'from-green-500 to-emerald-500',
    purple: 'from-purple-500 to-pink-500',
  };

  return (
    <div className="flex items-center justify-between mb-2">
      <span className="text-gray-400 text-sm flex-1">{label}</span>
      <div className="flex-1 mx-3">
        <div className="h-2 rounded-full bg-gray-800 overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${percent}%` }}
            transition={{ duration: 0.8 }}
            className={`h-full rounded-full bg-gradient-to-r ${colorClasses[color]}`}
          />
        </div>
      </div>
      <span className="text-white font-medium w-8 text-right">{value}</span>
    </div>
  );
};

// Module Visiteurs principal
const SpaceVisitors = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSession, setExpandedSession] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/visitors`);
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error('Erreur chargement visiteurs:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchData, 30000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [fetchData, autoRefresh]);

  const markAlertRead = async (alertId) => {
    try {
      await fetch(`${API_URL}/api/admin/security-alerts/${alertId}/read`, {
        method: 'POST'
      });
      fetchData();
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) return `${mins}m ${secs}s`;
    return `${secs}s`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'mobile': return <Smartphone className="w-4 h-4" />;
      case 'tablet': return <Tablet className="w-4 h-4" />;
      default: return <Monitor className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <Radio className="w-12 h-12 text-red-400 animate-pulse mx-auto mb-4" />
          <p className="text-gray-400">Scan en cours...</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-12 backdrop-blur-xl rounded-2xl border border-red-500/30 bg-[#12121a]/60">
        <WifiOff className="w-12 h-12 text-red-400 mx-auto mb-4" />
        <p className="text-gray-400">Signal perdu - Erreur de chargement</p>
        <Button onClick={fetchData} className="mt-4 bg-red-500/20 text-red-400 border border-red-500/30">
          <RefreshCw className="w-4 h-4 mr-2" />
          Réessayer
        </Button>
      </div>
    );
  }

  const totalDevices = Object.values(data.deviceStats || {}).reduce((a, b) => a + b, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-500">
            👁️ VISITEURS
          </h1>
          <p className="text-gray-500 text-sm">Surveillance en temps réel</p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded accent-red-500"
            />
            <Wifi className={`w-4 h-4 ${autoRefresh ? 'text-green-400' : 'text-gray-500'}`} />
            Auto-refresh
          </label>
          <Button
            onClick={fetchData}
            variant="outline"
            className="border-red-500/30 text-red-400"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Scanner
          </Button>
        </div>
      </div>

      {/* Alertes de sécurité */}
      <AnimatePresence>
        {data.securityAlerts && data.securityAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="backdrop-blur-xl rounded-2xl p-4 border-2 border-red-500/50 bg-red-500/10"
          >
            <h3 className="text-red-400 font-bold flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 animate-pulse" />
              ⚠️ ALERTES DE SÉCURITÉ ({data.alertCount})
            </h3>
            <div className="space-y-2">
              {data.securityAlerts.map((alert) => (
                <div 
                  key={alert.id}
                  className="bg-red-950/50 rounded-xl p-3 flex items-start justify-between"
                >
                  <div>
                    <p className="text-white font-medium">{alert.title}</p>
                    <p className="text-red-300 text-sm">{alert.message}</p>
                    <p className="text-red-400/60 text-xs mt-1">{formatTime(alert.createdAt)}</p>
                  </div>
                  <Button
                    onClick={() => markAlertRead(alert.id)}
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300"
                  >
                    Marquer lu
                  </Button>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats principales */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={Eye}
          label="En ligne"
          value={data.activeNow || 0}
          color="green"
          pulse
        />
        <StatCard
          icon={Users}
          label="Aujourd'hui"
          value={data.todayStats?.totalSessions || 0}
          subValue={`${data.todayStats?.uniqueVisitors || 0} uniques`}
          color="blue"
        />
        <StatCard
          icon={Clock}
          label="Durée moy."
          value={data.todayStats?.avgDurationFormatted || '0s'}
          color="purple"
        />
        <StatCard
          icon={Shield}
          label="Alertes"
          value={data.alertCount || 0}
          color={data.alertCount > 0 ? 'red' : 'amber'}
        />
      </div>

      {/* Stats détaillées */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Par appareil */}
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-gray-700 bg-[#12121a]/60">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-cyan-400" />
            Par appareil
          </h3>
          {Object.entries(data.deviceStats || {}).map(([device, count]) => (
            <ProgressBar
              key={device}
              label={device === 'mobile' ? '📱 Mobile' : device === 'tablet' ? '📱 Tablette' : '🖥️ Desktop'}
              value={count}
              max={totalDevices}
              color="cyan"
            />
          ))}
        </div>

        {/* Par pays */}
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-gray-700 bg-[#12121a]/60">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-green-400" />
            Par pays
          </h3>
          {Object.entries(data.countryStats || {}).slice(0, 5).map(([country, count]) => (
            <div key={country} className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{country}</span>
              <span className="text-white font-medium">{count}</span>
            </div>
          ))}
          {Object.keys(data.countryStats || {}).length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">Aucune donnée</p>
          )}
        </div>

        {/* Top villes */}
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-gray-700 bg-[#12121a]/60">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-purple-400" />
            Top villes
          </h3>
          {(data.topCities || []).slice(0, 5).map(([city, count]) => (
            <div key={city} className="flex items-center justify-between mb-2">
              <span className="text-gray-400 text-sm">{city}</span>
              <span className="text-white font-medium">{count}</span>
            </div>
          ))}
          {(data.topCities || []).length === 0 && (
            <p className="text-gray-500 text-sm text-center py-4">Aucune donnée</p>
          )}
        </div>
      </div>

      {/* Visiteurs actifs */}
      {data.activeSessions && data.activeSessions.length > 0 && (
        <div className="backdrop-blur-xl rounded-2xl p-4 border border-green-500/30 bg-[#12121a]/60">
          <h3 className="text-green-400 font-bold mb-4 flex items-center gap-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <Activity className="w-4 h-4" />
            Visiteurs actifs maintenant ({data.activeSessions.length})
          </h3>
          <div className="space-y-2">
            {data.activeSessions.map((session) => (
              <ActiveSessionCard
                key={session.sessionId}
                session={session}
                getDeviceIcon={getDeviceIcon}
                formatDuration={formatDuration}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sessions récentes */}
      <div className="backdrop-blur-xl rounded-2xl p-4 border border-gray-700 bg-[#12121a]/60">
        <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-cyan-400" />
          Sessions récentes
        </h3>
        
        {(data.recentSessions || []).length === 0 ? (
          <p className="text-gray-500 text-center py-8">Aucune session récente</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-500 text-left border-b border-gray-700">
                  <th className="pb-3 font-medium">Heure</th>
                  <th className="pb-3 font-medium">Appareil</th>
                  <th className="pb-3 font-medium">Localisation</th>
                  <th className="pb-3 font-medium">Durée</th>
                  <th className="pb-3 font-medium">Pages</th>
                  <th className="pb-3 font-medium">Type</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {data.recentSessions.map((session) => (
                  <React.Fragment key={session.sessionId}>
                    <tr 
                      className="text-gray-300 hover:bg-gray-700/30 cursor-pointer transition-colors"
                      onClick={() => setExpandedSession(
                        expandedSession === session.sessionId ? null : session.sessionId
                      )}
                    >
                      <td className="py-3">{formatTime(session.startTime)}</td>
                      <td className="py-3">
                        <span className="flex items-center gap-2">
                          {getDeviceIcon(session.deviceType)}
                          {session.browser}
                        </span>
                      </td>
                      <td className="py-3">{session.city}, {session.country}</td>
                      <td className="py-3">{formatDuration(session.duration)}</td>
                      <td className="py-3">{session.pageViews}</td>
                      <td className="py-3">
                        {session.customerId ? (
                          <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 rounded-full text-xs font-medium">
                            Client
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-500/20 text-gray-400 rounded-full text-xs">
                            Visiteur
                          </span>
                        )}
                      </td>
                    </tr>
                    <AnimatePresence>
                      {expandedSession === session.sessionId && (
                        <motion.tr
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                        >
                          <td colSpan="6" className="py-3 px-4 bg-[#1a1a25] rounded-b-xl">
                            <div className="text-xs text-gray-400 grid grid-cols-2 md:grid-cols-3 gap-2">
                              <p><strong className="text-gray-300">IP:</strong> {session.ip}</p>
                              <p><strong className="text-gray-300">ISP:</strong> {session.isp}</p>
                              <p><strong className="text-gray-300">Appareil:</strong> {session.device}</p>
                              <p><strong className="text-gray-300">Écran:</strong> {session.screenWidth}x{session.screenHeight}</p>
                              <p><strong className="text-gray-300">Première page:</strong> {session.firstPage}</p>
                              {session.referrer && <p><strong className="text-gray-300">Source:</strong> {session.referrer}</p>}
                            </div>
                            {session.pagesVisited && session.pagesVisited.length > 0 && (
                              <div className="mt-2 pt-2 border-t border-gray-700">
                                <p className="text-gray-300 text-xs mb-1"><strong>Pages visitées:</strong></p>
                                <div className="flex flex-wrap gap-1">
                                  {session.pagesVisited.map((page, idx) => (
                                    <span key={idx} className="px-2 py-0.5 bg-gray-700 rounded text-gray-400 text-xs">
                                      {page}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </td>
                        </motion.tr>
                      )}
                    </AnimatePresence>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpaceVisitors;
